<docs>
The floating menu surface: a list of rows anchored to any trigger you give it (Reka DropdownMenu).
You supply the trigger through `#trigger` (a ZoneButton, a ZoneIconButton, a search field, a table
cell's kebab, a plain `<button>`, anything with a single root element); the panel owns the surface,
the rows, the keyboard navigation and the ARIA wiring. Reach for ZoneButtonDropdown first (this menu
with a ZoneButton trigger already wired, for a plain list of actions), and for ZoneFloatingMenu when
you need the richer rows (checkboxes, radios, avatars, groups, search, loading) or a trigger that is
not a button. ZonePopover holds free-form panel content, ZoneSelect is a labelled form field that
holds a value, ZoneTooltip a hover hint.

## Items

`{ id, label?, description?, icon?, trailingIcon?, avatar?, href?, disabled?, tooltip?, type?, items? }`

- `type` omitted / `'item'`: a normal row. Give it `items` and the row becomes a submenu parent
  instead (trailing chevron, panel opens to the side).
- `type: 'group'`: a section; `label` renders as the small header and `items` as its rows. Groups do
  not nest.
- `type: 'separator'`: a divider row.
- Any extra keys you put on an item come back untouched on the `select` payload, so a tracking id or
  a whole row object can ride along.

## v-model

`v-model` is the open state (Boolean), optional: leave it off and the menu owns its own open state
(`defaultOpen` sets the initial value); pass it and the menu is fully controlled, the trigger only
emits until you write back. `v-model:selected` is which rows read as chosen: an array of ids under
`selectable="multiple"`, one id under `"single"`. `v-model:search` is the query in the built-in
search box; leave it off and the menu keeps the query itself.

## Notes

- The panel teleports to `#zone-teleport` (carries `.w-tw` and z-index 8030), so it escapes any
  overflow/clipping ancestor; this is the menu to use inside a ZoneTable cell. In tests, query
  `document.body`, not the wrapper. Rows only exist while open: assert on the trigger or on `items`,
  or click first.
- `inheritAttrs: false`: attributes land on the trigger, so `aria-label="…"` on this component
  reaches the real trigger element.
- Selection is controlled. The menu never mutates `selected`: read `update:selected` and write it
  back, or nothing visibly changes.
- `selected` is honoured even with `selectable="none"`. That renders Figma's Selected row (indigo-50
  background, primary-700 leading icon) with no checkbox or radio: use it to mark "the one you are
  looking at" in a navigation menu.
- Highlight and selection are different states, per Figma: hover/arrow-key highlight is gray-50, a
  selected row is indigo-50 and keeps it while highlighted.
- Submenus are one level deep. An `items` array on a submenu child is ignored, the same limit
  ZoneButtonDropdown carries, and for the same reason: deeper nesting wants a recursive child
  component, which nothing needs yet.
- Group headers render as plain label rows, not a `role="group"` wrapper: Figma draws them as a row
  in the same list, and flattening keeps one row implementation instead of three.
- An uncontrolled search query is cleared when the menu closes. A controlled one is left exactly as
  the caller set it.
- `autoFilter` matches `label` and `description`, case-insensitively. A group keeps only its matching
  children and disappears when none match; separators drop out of a filtered list; a submenu parent
  matches on its own label, its children are not searched.
- Not a form field: no `name`, no hidden input. Put the selection in your own state and submit it
  however the surrounding form does.
- The scroll area uses the native scrollbar, styled to Figma's 14px gray track / 6px thumb via
  `::-webkit-scrollbar`; Firefox gets its own thin scrollbar. Reka's arrow-key navigation scrolls the
  native container, so nothing is lost.

Figma: [Floating menu](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=4966-2093)
</docs>

<template>
  <DropdownMenuRoot
    :open="isOpen"
    :modal="modal"
    @update:open="onOpenChange"
  >
    <DropdownMenuTrigger
      as-child
      :disabled="disabled"
      v-bind="$attrs"
    >
      <slot
        name="trigger"
        :open="isOpen"
      />
    </DropdownMenuTrigger>

    <!--
      Target `#zone-teleport` (defined in the app layouts, see application.scss) rather than the
      reka-ui default of `document.body`. It already carries `.w-tw` — required for Tailwind
      utilities to apply at all, since `important: '.w-tw'` in tailwind.config.js makes `.w-tw` a
      required ancestor — plus the fixed positioning and z-index (8030) every other piece of
      floating Zone UI in this app relies on.
    -->
    <DropdownMenuPortal to="#zone-teleport">
      <DropdownMenuContent
        :side="side"
        :align="align"
        :side-offset="sideOffset"
        :align-offset="alignOffset"
        :class="panelClasses"
        :style="panelStyle"
      >
        <div
          v-if="searchable"
          class="border-b border-gray-200 p-4"
        >
          <div class="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 focus-within:border-primary-700 focus-within:ring-1 focus-within:ring-primary-700">
            <ZoneIcon
              icon="search"
              size="lg"
              class="shrink-0 text-gray-500"
            />
            <!--
              Reka's filter input rather than a bare <input>: it hands ArrowUp/ArrowDown/Home/End
              and Enter back to the menu's own roving focus, so the list stays keyboard-navigable
              while the caret is in the box, and it silences the menu's type-ahead that would
              otherwise fight every keystroke.
            -->
            <DropdownMenuFilter
              auto-focus
              :model-value="resolvedSearch"
              :placeholder="searchPlaceholder"
              class="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-gray-700 placeholder:font-regular placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0"
              @update:model-value="onSearchUpdate"
            />
          </div>
        </div>

        <slot name="header" />

        <!--
          The scrolling region doubles as the radio group when `selectable="single"` — Reka's
          RadioGroup renders a plain element and forwards attrs, so one node can be both instead of
          adding a wrapper that only exists in one of the three selection modes.
        -->
        <component
          :is="viewportComponent"
          :ref="revealSelected"
          v-bind="viewportBindings"
          data-testid="floating-menu-viewport"
          :class="VIEWPORT_CLASSES"
          :style="viewportStyle"
        >
          <slot>
            <template v-if="loading">
              <div
                v-for="row in loadingRows"
                :key="`skeleton-${row}`"
                data-testid="floating-menu-skeleton"
                class="flex items-center border-b border-gray-200 px-4 py-3"
              >
                <span class="h-4 w-full animate-pulse rounded bg-gray-100" />
              </div>
            </template>

            <div
              v-else-if="isEmpty"
              data-testid="floating-menu-empty"
              class="px-4 py-8 text-center"
            >
              <slot name="empty">
                <p class="text-sm font-medium text-gray-900">{{ emptyTitle }}</p>
                <p
                  v-if="resolvedEmptyDescription"
                  class="mt-1 text-xs text-gray-500"
                >{{ resolvedEmptyDescription }}</p>
              </slot>
            </div>

            <template v-else>
              <template
                v-for="entry in renderEntries"
                :key="entry.key"
              >
                <DropdownMenuLabel
                  v-if="entry.kind === 'label'"
                  data-testid="floating-menu-group-label"
                  :class="LABEL_CLASSES"
                >{{ entry.label }}</DropdownMenuLabel>

                <DropdownMenuSeparator
                  v-else-if="entry.kind === 'separator'"
                  class="h-px bg-gray-200"
                />

                <DropdownMenuSub v-else-if="entry.kind === 'submenu'">
                  <!--
                    A submenu parent is never an anchor and never carries a selection affordance —
                    its job is to open the panel, so an href or a checkbox on it would be a second,
                    competing activation.
                  -->
                  <DropdownMenuSubTrigger
                    as-child
                    :disabled="entry.item.disabled"
                  >
                    <div
                      data-testid="floating-menu-submenu-trigger"
                      :title="entry.item.tooltip || undefined"
                      :class="[ROW_CLASSES, rowStateClasses(entry.item)]"
                    >
                      <slot
                        name="item"
                        :item="entry.item"
                        :selected="isSelected(entry.item)"
                      >
                        <ZoneIcon
                          v-if="entry.item.icon"
                          :icon="entry.item.icon"
                          size="lg"
                          :class="rowIconClasses(entry.item)"
                        />
                        <span class="flex min-w-0 flex-1 flex-col">
                          <span
                            data-testid="floating-menu-item-label"
                            class="block truncate text-sm text-gray-900"
                          >{{ entry.item.label }}</span>
                        </span>
                      </slot>
                      <ZoneIcon
                        icon="keyboard_arrow_right"
                        size="lg"
                        class="shrink-0 text-gray-600"
                      />
                    </div>
                  </DropdownMenuSubTrigger>

                  <DropdownMenuPortal to="#zone-teleport">
                    <DropdownMenuSubContent
                      :side-offset="sideOffset"
                      :class="PANEL_CLASSES"
                    >
                      <!--
                        A plain div, NOT `viewportComponent`, and deliberately so. Under
                        `selectable="single"` the rows here are DropdownMenuRadioItems, which need
                        a RadioGroup — and they already have the root one: provide/inject follows
                        the COMPONENT tree, and this SubContent sits inside the root viewport's
                        slot there however far the portal moves its DOM. Repeating the group would
                        nest a second one bound to the same model and split one radio set into two
                        ARIA groups, which is what the selection actually is not. Spec'd below.
                      -->
                      <div
                        :class="VIEWPORT_CLASSES"
                        :style="viewportStyle"
                      >
                        <!--
                          The row body is spelled out a second time here rather than pulled into a
                          shared child component: Reka needs its own Item part inside SubContent, and
                          one level of nesting does not yet justify the indirection (same call as
                          ZoneButtonDropdown). Keep the two in step when either changes.
                        -->
                        <component
                          :is="rowComponent"
                          v-for="child in entry.item.items"
                          :key="child.id"
                          v-bind="rowBindings(child)"
                          as-child
                          @select="onRowSelect(child, $event)"
                        >
                          <component
                            :is="rowTag(child)"
                            data-testid="floating-menu-item"
                            :href="rowHref(child)"
                            :data-selected="isSelected(child) || undefined"
                            :title="child.tooltip || undefined"
                            :class="[ROW_CLASSES, rowStateClasses(child)]"
                          >
                            <span
                              v-if="isMultiple"
                              data-testid="floating-menu-checkbox"
                              :class="checkboxClasses(child)"
                            >
                              <ZoneIcon
                                v-if="isSelected(child)"
                                icon="check"
                                size="xs"
                              />
                            </span>
                            <span
                              v-else-if="isSingle"
                              data-testid="floating-menu-radio"
                              :class="radioClasses(child)"
                            >
                              <span
                                v-if="isSelected(child)"
                                class="size-2 rounded-full bg-primary-500"
                              />
                            </span>

                            <slot
                              name="item"
                              :item="child"
                              :selected="isSelected(child)"
                            >
                              <ZoneIcon
                                v-if="child.icon"
                                :icon="child.icon"
                                size="lg"
                                :class="rowIconClasses(child)"
                              />
                              <img
                                v-if="child.avatar"
                                :src="child.avatar"
                                :alt="child.label"
                                class="size-7 shrink-0 rounded-full object-cover"
                              />
                              <span class="flex min-w-0 flex-1 flex-col">
                                <span
                                  data-testid="floating-menu-item-label"
                                  :class="rowLabelClasses(child)"
                                >{{ child.label }}</span>
                                <span
                                  v-if="child.description"
                                  class="block truncate text-xs text-gray-500"
                                >{{ child.description }}</span>
                              </span>
                              <ZoneIcon
                                v-if="child.trailingIcon"
                                :icon="child.trailingIcon"
                                size="lg"
                                class="shrink-0 text-gray-600"
                              />
                            </slot>
                          </component>
                        </component>
                      </div>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>

                <!--
                  `as-child` so a row carrying `href` can BE the anchor rather than wrap one: Reka
                  merges its roving-focus props, data-highlighted and ARIA onto whatever renders here.
                -->
                <component
                  :is="rowComponent"
                  v-else
                  v-bind="rowBindings(entry.item)"
                  as-child
                  @select="onRowSelect(entry.item, $event)"
                >
                  <component
                    :is="rowTag(entry.item)"
                    data-testid="floating-menu-item"
                    :href="rowHref(entry.item)"
                    :data-selected="isSelected(entry.item) || undefined"
                    :title="entry.item.tooltip || undefined"
                    :class="[ROW_CLASSES, rowStateClasses(entry.item)]"
                  >
                    <span
                      v-if="isMultiple"
                      data-testid="floating-menu-checkbox"
                      :class="checkboxClasses(entry.item)"
                    >
                      <ZoneIcon
                        v-if="isSelected(entry.item)"
                        icon="check"
                        size="xs"
                      />
                    </span>
                    <span
                      v-else-if="isSingle"
                      data-testid="floating-menu-radio"
                      :class="radioClasses(entry.item)"
                    >
                      <span
                        v-if="isSelected(entry.item)"
                        class="size-2 rounded-full bg-primary-500"
                      />
                    </span>

                    <slot
                      name="item"
                      :item="entry.item"
                      :selected="isSelected(entry.item)"
                    >
                      <ZoneIcon
                        v-if="entry.item.icon"
                        :icon="entry.item.icon"
                        size="lg"
                        :class="rowIconClasses(entry.item)"
                      />
                      <img
                        v-if="entry.item.avatar"
                        :src="entry.item.avatar"
                        :alt="entry.item.label"
                        class="size-7 shrink-0 rounded-full object-cover"
                      />
                      <span class="flex min-w-0 flex-1 flex-col">
                        <span
                          data-testid="floating-menu-item-label"
                          :class="rowLabelClasses(entry.item)"
                        >{{ entry.item.label }}</span>
                        <span
                          v-if="entry.item.description"
                          class="block truncate text-xs text-gray-500"
                        >{{ entry.item.description }}</span>
                      </span>
                      <ZoneIcon
                        v-if="entry.item.trailingIcon"
                        :icon="entry.item.trailingIcon"
                        size="lg"
                        class="shrink-0 text-gray-600"
                      />
                    </slot>
                  </component>
                </component>
              </template>
            </template>
          </slot>
        </component>

        <slot name="footer" />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script lang="ts">
import { computed, ref, type Component, type CSSProperties, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuFilter,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from 'reka-ui';
import ZoneIcon, { type IconName } from './Icon.vue';

export type FloatingMenuSide = 'top' | 'right' | 'bottom' | 'left';
export type FloatingMenuAlign = 'start' | 'center' | 'end';
export type FloatingMenuSelectable = 'none' | 'single' | 'multiple';
export type FloatingMenuItemType = 'item' | 'group' | 'separator';
export type FloatingMenuItemId = string | number;
export type FloatingMenuSelection = FloatingMenuItemId | FloatingMenuItemId[] | null;

export interface FloatingMenuItem {
  id: FloatingMenuItemId;
  /** Omitted or 'item' for a row; 'group' for a section header plus its rows; 'separator' for a rule. */
  type?: FloatingMenuItemType;
  label?: string;
  /** Second line, smaller and gray. The email under a member's name, in Figma's avatar rows. */
  description?: string;
  /** A Zone icon name (see ZoneIcon), drawn before the label. */
  icon?: IconName;
  /** A Zone icon name drawn after the label. Submenu parents get their own chevron instead. */
  trailingIcon?: IconName;
  /** Image URL for a round 28px avatar before the label. */
  avatar?: string;
  /**
   * Renders the row as a real anchor, so middle-click, ctrl-click and "open in new tab" behave
   * normally. Navigation is the browser's — `select` still fires. Ignored while `disabled`, which
   * would otherwise leave a followable link the user cannot see is off, and ignored on a submenu
   * parent, whose activation already means "open the panel".
   */
  href?: string;
  disabled?: boolean;
  /**
   * Native title on the row. Chiefly for explaining WHY a row is disabled — a disabled row has no
   * other way to say so. Deliberately the native tooltip and not ZoneTooltip: floating-vue is
   * registered without a `container`, so its popper lands outside `.w-tw` and renders unstyled.
   */
  tooltip?: string;
  /** Rows of a `type: 'group'` section, or — on a plain row — the one level of submenu. */
  items?: FloatingMenuItem[];
  /** Anything else you attach rides along untouched on the `select` payload. */
  [key: string]: unknown;
}

type RenderEntry =
  | { kind: 'label'; key: string; label: string }
  | { kind: 'separator'; key: string }
  | { kind: 'row'; key: string; item: FloatingMenuItem }
  | { kind: 'submenu'; key: string; item: FloatingMenuItem };

const SIDES: FloatingMenuSide[] = ['top', 'right', 'bottom', 'left'];
const ALIGNS: FloatingMenuAlign[] = ['start', 'center', 'end'];
const SELECTABLES: FloatingMenuSelectable[] = ['none', 'single', 'multiple'];

// The Figma surface: white, 1px gray/300 border, rounded-md, shadow-md, clipped so the first and
// last rows follow the corner radius.
const PANEL_CLASSES =
  'z-50 min-w-[200px] max-w-[90vw] overflow-hidden rounded-md border border-gray-300 bg-white shadow-md focus:outline-none';

// Figma draws a 14px track (gray/50, 1px gray/200 on its inner edge) holding a 6px black/20 thumb.
// A 4px transparent border plus `background-clip: content-box` is how a 6px thumb is drawn inside a
// 14px track without a scrollbar plugin. Firefox ignores ::-webkit-* and falls back to its own thin
// scrollbar, which is close enough that it is not worth a second code path.
const SCROLLBAR_CLASSES = [
  '[&::-webkit-scrollbar]:w-[14px]',
  '[&::-webkit-scrollbar-track]:border-l',
  '[&::-webkit-scrollbar-track]:border-gray-200',
  '[&::-webkit-scrollbar-track]:bg-gray-50',
  '[&::-webkit-scrollbar-thumb]:rounded-full',
  '[&::-webkit-scrollbar-thumb]:border-4',
  '[&::-webkit-scrollbar-thumb]:border-transparent',
  '[&::-webkit-scrollbar-thumb]:bg-black/20',
  '[&::-webkit-scrollbar-thumb]:bg-clip-content',
].join(' ');

const VIEWPORT_CLASSES = `overflow-y-auto overflow-x-hidden ${SCROLLBAR_CLASSES}`;

// px-4 py-3, 8px gap, 14px text, a gray/200 rule under every row — Figma's Items component.
const ROW_CLASSES =
  'flex w-full items-center gap-2 border-b border-gray-200 px-4 py-3 text-left text-sm text-gray-900 no-underline hover:text-gray-900 select-none outline-none';

// 12px semi-bold gray/800 on white, half the vertical padding of a row.
const LABEL_CLASSES = 'truncate bg-white px-4 py-1.5 text-xs font-semibold text-gray-800';

const CHECKBOX_BASE = 'flex size-4 shrink-0 items-center justify-center rounded border';

// The checked box is Figma's Blue 700, the same fill ZoneCheckbox uses — NOT the primary-500 that
// ZoneButton's solid colour uses. `border-transparent` over dropping the border keeps the box a
// flat 16px in both states, so it never shifts by a pixel as it fills. The menu draws its own
// affordance rather than nesting a ZoneCheckbox, because Reka's DropdownMenuCheckboxItem already
// owns the row's checked state and a real <input> inside it would be a second, competing control
// — so the two have to be kept in step by hand. Keep this pair equal to ZoneCheckbox's
// `boxColorClasses`.
const CHECKBOX_CHECKED = 'border-transparent bg-primary-700 text-white';
const CHECKBOX_UNCHECKED = 'border-gray-300 bg-white';
const RADIO_BASE = 'flex size-4 shrink-0 items-center justify-center rounded-full border';
</script>

<script setup lang="ts">
/**
 * DropdownMenuRoot renders no element of its own (it is a pure provider), so inherited attrs
 * would have nowhere to land and Vue would drop them with a warning. Forwarding them to the
 * trigger is also what makes `aria-label` usable on a glyph-only trigger.
 */
defineOptions({ name: 'ZoneFloatingMenu', inheritAttrs: false });

const { t } = useI18n();

const props = defineProps({
  /** The rows; see the Items section. */
  items: {
    type: Array as PropType<FloatingMenuItem[]>,
    default: () => [],
  },
  /**
   * Open state, as v-model. Left unset (null) the menu owns its own state; pass it — even as
   * `false` — and it becomes fully controlled, so the trigger only emits and the caller decides.
   * `default: null` rather than `false` is what keeps Vue from boolean-casting an absent prop.
   */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /** Initial open state while uncontrolled. Ignored once `modelValue` is passed. */
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  /**
   * `none`: plain action rows, no affordance. `single`: a radio dot per row, `selected` is one id.
   * `multiple`: a checkbox per row, `selected` is an array of ids.
   */
  selectable: {
    type: String as PropType<FloatingMenuSelectable>,
    default: 'none',
    validator: (value: string) => SELECTABLES.includes(value as FloatingMenuSelectable),
  },
  /**
   * Which rows read as chosen, as `v-model:selected`. An array under selectable='multiple', one
   * id under 'single'. Always controlled: the menu emits the next value and never mutates this.
   */
  selected: {
    type: [String, Number, Array] as PropType<FloatingMenuSelection>,
    default: null,
  },
  /**
   * A search box pinned above the rows, wired into the menu's own arrow-key navigation (Reka's
   * DropdownMenuFilter).
   */
  searchable: {
    type: Boolean,
    default: false,
  },
  /** The query, as `v-model:search`. Left unset (null) the menu keeps it internally. */
  search: {
    type: String,
    default: null,
  },
  /** Placeholder of the search box. */
  searchPlaceholder: {
    type: String,
    default: null,
  },
  /** Filter `items` locally on the query. Turn it off when the caller fetches filtered results. */
  autoFilter: {
    type: Boolean,
    default: true,
  },
  /** Skeleton rows instead of the list. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** How many skeleton rows `loading` draws. */
  loadingRows: {
    type: Number,
    default: 6,
  },
  /** Title of the built-in "nothing found" state. */
  emptyTitle: {
    type: String,
    default: null,
  },
  /** Null falls back to the search hint while a query is active, and to nothing otherwise. */
  emptyDescription: {
    type: String,
    default: null,
  },
  /** Which side of the trigger the panel opens on. */
  side: {
    type: String as PropType<FloatingMenuSide>,
    default: 'bottom',
    validator: (value: string) => SIDES.includes(value as FloatingMenuSide),
  },
  /**
   * `start`, not `center`: the spec's do's-and-don'ts call out centre-aligning a floating menu on
   * its trigger as a Don't — align it left or right depending on where it sits in the UI.
   */
  align: {
    type: String as PropType<FloatingMenuAlign>,
    default: 'start',
    validator: (value: string) => ALIGNS.includes(value as FloatingMenuAlign),
  },
  /** Gap in px from the trigger. */
  sideOffset: {
    type: Number,
    default: 4,
  },
  /** Shift in px along the align axis. */
  alignOffset: {
    type: Number,
    default: 0,
  },
  /**
   * A CSS width for the panel, applied inline. Inline rather than a Tailwind arbitrary class so a
   * dynamic value isn't dropped by the JIT compiler — same reasoning as ZonePopover.
   */
  width: {
    type: String,
    default: null,
  },
  /** Take the trigger's width, for a menu that reads as part of the field above it. */
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  /** CSS max-height of the scrolling area. */
  maxHeight: {
    type: String,
    default: '320px',
  },
  /**
   * Whether choosing a row closes the menu. Null lets the selection mode decide: a multi-select
   * menu stays open so several rows can be ticked, anything else closes.
   */
  closeOnSelect: {
    type: Boolean,
    default: null,
  },
  /** Trap focus and block interaction outside the panel while it is open. Reka's default. */
  modal: {
    type: Boolean,
    default: true,
  },
  /** Stops the trigger opening the menu. Closing is never blocked. */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /** The new open state. */
  'update:modelValue': [open: boolean];
  /** The new selection, for `v-model:selected`. */
  'update:selected': [selection: FloatingMenuSelection];
  /** The new query, for `v-model:search`. */
  'update:search': [query: string];
  /**
   * The full item object of the activated row. Fires for plain, checkbox and radio rows alike; never for a
   * disabled row, nor for a submenu parent (opening one is navigation).
   */
  select: [item: FloatingMenuItem];
  /** It opened. */
  open: [];
  /** It closed. */
  close: [];
}>();

defineSlots<{
  /** The anchor. Rendered with Reka's `as-child`, so it must have exactly one root element; a bare string will not do. */
  trigger?(props: { open: boolean }): any;
  /**
   * Replaces a row's content (icon, avatar, text, trailing icon). The row element, its click/keyboard
   * behaviour and the checkbox/radio affordance are still the menu's.
   */
  item?(props: { item: FloatingMenuItem; selected: boolean }): any;
  /** A fixed region above the scrolling rows (below the search box). */
  header?(): any;
  /** A fixed region below the scrolling rows. */
  footer?(): any;
  /** Replaces the built-in "nothing found" copy. */
  empty?(): any;
  /** Replaces the whole body, rows, loading and empty state included: the escape hatch for a panel that is not a list. */
  default?(): any;
}>();

const uncontrolledOpen = ref(props.defaultOpen);
const localSearch = ref('');

const isControlled = computed<boolean>(() => props.modelValue !== null);

const isOpen = computed<boolean>(() => (isControlled.value ? props.modelValue : uncontrolledOpen.value));

const isMultiple = computed<boolean>(() => props.selectable === 'multiple');

const isSingle = computed<boolean>(() => props.selectable === 'single');

const resolvedSearch = computed<string>(() => (props.search !== null ? props.search : localSearch.value));

const resolvedCloseOnSelect = computed<boolean>(() => {
  if (props.closeOnSelect !== null) {
    return props.closeOnSelect;
  }

  return !isMultiple.value;
});

/** Normalised to an array so one `isSelected` covers single, multiple and the unset case. */
const selectedIds = computed<FloatingMenuItemId[]>(() => {
  if (props.selected === null || props.selected === undefined) {
    return [];
  }

  return Array.isArray(props.selected) ? props.selected : [props.selected];
});

/** What Reka's RadioGroup wants: one value, or null for "nothing chosen". */
const scalarSelected = computed<FloatingMenuItemId | null>(() => (selectedIds.value.length ? selectedIds.value[0] : null));

/** Reka's Item part for the current selection mode — each one carries its own ARIA role. */
const rowComponent = computed<Component>(() => {
  if (isMultiple.value) { return DropdownMenuCheckboxItem; }
  if (isSingle.value) { return DropdownMenuRadioItem; }

  return DropdownMenuItem;
});

const viewportComponent = computed<Component | string>(() => (isSingle.value ? DropdownMenuRadioGroup : 'div'));

function onRadioUpdate(value: FloatingMenuItemId) {
  emit('update:selected', value);
}

const viewportBindings = computed<Record<string, unknown>>(() => {
  if (!isSingle.value) {
    return {};
  }

  return {
    modelValue: scalarSelected.value,
    'onUpdate:modelValue': onRadioUpdate,
  };
});

const filteredItems = computed<FloatingMenuItem[]>(() => {
  const query = resolvedSearch.value.trim().toLowerCase();

  if (!props.searchable || !props.autoFilter || !query) {
    return props.items;
  }

  const matches = (item: FloatingMenuItem) =>
    [item.label, item.description].some((text) => (text || '').toLowerCase().includes(query));

  const kept: FloatingMenuItem[] = [];

  props.items.forEach((item) => {
    // Separators only mean something between rows the user can still see.
    if (item.type === 'separator') { return; }

    if (item.type === 'group') {
      const children = (item.items || []).filter(matches);

      if (children.length) { kept.push({ ...item, items: children }); }

      return;
    }

    if (matches(item)) { kept.push(item); }
  });

  return kept;
});

/**
 * Groups are flattened into a header entry followed by their rows, rather than wrapped in a
 * Reka Group: Figma draws the header as another row of the same list, and a flat sequence keeps
 * ONE row implementation instead of one per nesting level.
 */
const renderEntries = computed<RenderEntry[]>(() => {
  const entries: RenderEntry[] = [];

  const pushLeaf = (item: FloatingMenuItem, key: string) => {
    const kind = Array.isArray(item.items) && item.items.length ? 'submenu' : 'row';

    entries.push({ kind, key, item } as RenderEntry);
  };

  filteredItems.value.forEach((item, index) => {
    const key = `${item.id ?? index}`;

    if (item.type === 'separator') {
      entries.push({ kind: 'separator', key });

      return;
    }

    if (item.type === 'group') {
      if (item.label) {
        entries.push({ kind: 'label', key: `${key}-label`, label: item.label });
      }

      (item.items || []).forEach((child, childIndex) => {
        pushLeaf(child, `${key}-${child.id ?? childIndex}`);
      });

      return;
    }

    pushLeaf(item, key);
  });

  return entries;
});

const isEmpty = computed<boolean>(() => (
  !props.loading && !renderEntries.value.some((entry) => entry.kind === 'row' || entry.kind === 'submenu')
));

const searchPlaceholder = computed<string>(() => props.searchPlaceholder ?? t('search_placeholder'));
const emptyTitle = computed<string>(() => props.emptyTitle ?? t('empty_title'));
const resolvedEmptyDescription = computed<string | null>(() => {
  if (props.emptyDescription !== null) {
    return props.emptyDescription;
  }

  return resolvedSearch.value.trim() ? t('search_empty_description') : null;
});

// A Tailwind class, not an inline style: `var()` in an inline width is fragile across
// browsers and test environments, and this value never varies per instance.
const panelClasses = computed<string>(() => (props.matchTriggerWidth
  ? `${PANEL_CLASSES} w-[var(--reka-dropdown-menu-trigger-width)]`
  : PANEL_CLASSES));

const panelStyle = computed<CSSProperties>(() => (props.width ? { width: props.width } : {}));

const viewportStyle = computed<CSSProperties>(() => ({ maxHeight: props.maxHeight }));

function isSelected(item: FloatingMenuItem): boolean {
  return selectedIds.value.includes(item.id);
}

// A disabled row never becomes an anchor: a real href would still be followable by middle-click
// and the keyboard, past the very state that is meant to block it.
function isLink(item: FloatingMenuItem): boolean {
  return !!item.href && !item.disabled;
}

function rowTag(item: FloatingMenuItem): string {
  return isLink(item) ? 'a' : 'div';
}

function rowHref(item: FloatingMenuItem): string | undefined {
  return isLink(item) ? item.href : undefined;
}

function onCheckedUpdate(item: FloatingMenuItem, checked: boolean) {
  const next = [...selectedIds.value];
  const index = next.indexOf(item.id);

  if (checked && index === -1) {
    next.push(item.id);
  } else if (!checked && index !== -1) {
    next.splice(index, 1);
  }

  emit('update:selected', next);
}

/** The props Reka's Item part for the current mode needs; the rest is identical across modes. */
function rowBindings(item: FloatingMenuItem): Record<string, unknown> {
  if (isMultiple.value) {
    return {
      disabled: item.disabled,
      textValue: item.label,
      modelValue: isSelected(item),
      'onUpdate:modelValue': (checked: boolean) => onCheckedUpdate(item, checked),
    };
  }

  if (isSingle.value) {
    return { disabled: item.disabled, textValue: item.label, value: item.id };
  }

  return { disabled: item.disabled, textValue: item.label };
}

/**
 * Hover/arrow-key highlight and selection are separate states in Figma — gray/50 and indigo/50
 * respectively — and a selected row keeps its indigo while highlighted. Both are resolved here
 * rather than stacked as two `data-*` variants, whose relative order in the compiled stylesheet
 * would decide the winner.
 */
function rowStateClasses(item: FloatingMenuItem): string {
  const classes = [item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'];

  // A disabled row keeps its selected background — being unavailable is not the same as being
  // unselected, and the opacity already says which it is.
  classes.push(
    isSelected(item)
      ? 'bg-indigo-50 data-[highlighted]:bg-indigo-50'
      : 'data-[highlighted]:bg-gray-50',
  );

  return classes.join(' ');
}

function rowIconClasses(item: FloatingMenuItem): string {
  return isSelected(item) ? 'shrink-0 text-primary-700' : 'shrink-0 text-gray-600';
}

function rowLabelClasses(item: FloatingMenuItem): string {
  // A two-line row leans on weight to separate name from description; a one-line row does not.
  return item.description
    ? 'block truncate text-sm font-medium text-gray-900'
    : 'block truncate text-sm text-gray-900';
}

function checkboxClasses(item: FloatingMenuItem): string {
  return `${CHECKBOX_BASE} ${isSelected(item) ? CHECKBOX_CHECKED : CHECKBOX_UNCHECKED}`;
}

function radioClasses(item: FloatingMenuItem): string {
  return isSelected(item)
    ? `${RADIO_BASE} border-primary-500 bg-white`
    : `${RADIO_BASE} border-gray-300 bg-white`;
}

/**
 * DropdownMenuRoot is always given a real boolean for `open`, so it runs controlled and this is
 * the single place the state can change — which is what lets `disabled` be enforced here rather
 * than relying on the caller's trigger element to swallow the click.
 */
function onOpenChange(value: boolean) {
  if (value && props.disabled) {
    return;
  }

  if (value === isOpen.value) {
    return;
  }

  if (!isControlled.value) {
    uncontrolledOpen.value = value;
  }

  // A stale query would silently hide rows the next time the menu opens. Only ours to clear —
  // a caller driving `search` owns theirs.
  if (!value) {
    localSearch.value = '';
  }

  emit('update:modelValue', value);
  emit(value ? 'open' : 'close');
}

/**
 * Function ref on the scroll viewport. Figma opens the menu ON the selected row (the Phone field's
 * current country), not on row one, so when the viewport mounts — the content is teleported and
 * only exists while open — it is scrolled so the selected row sits at the top. Runs once per
 * mount: Vue calls a function ref again on every render, and re-scrolling would fight the user.
 */
let revealedViewport: HTMLElement | null = null;

function revealSelected(target: unknown) {
  const root = target && typeof target === 'object' && '$el' in target
    ? (target as { $el: HTMLElement }).$el
    : (target as HTMLElement | null);

  if (!root || root === revealedViewport) {
    revealedViewport = root;

    return;
  }

  revealedViewport = root;

  // Reka positions the floating content a frame after it mounts; before that the rows have no
  // layout and every offset reads 0, so the scroll has to wait for the first painted frame.
  requestAnimationFrame(() => {
    const row = root.querySelector<HTMLElement>('[data-selected="true"]');

    if (row) {
      root.scrollTop = row.offsetTop - root.offsetTop;
    }
  });
}

function onSearchUpdate(value: string) {
  const next = value ?? '';

  if (props.search === null) {
    localSearch.value = next;
  }

  emit('update:search', next);
}

/**
 * Reka's Item emits `select` with a cancelable event and closes the menu unless it is prevented,
 * so this is where `closeOnSelect` is enforced. It runs BEFORE the checkbox/radio parts flip
 * their own model, which is why the selection itself is handled in `onCheckedUpdate` and
 * `onRadioUpdate`.
 */
function onRowSelect(item: FloatingMenuItem, event?: Event) {
  if (item.disabled) {
    return;
  }

  if (!resolvedCloseOnSelect.value) {
    event?.preventDefault();
  }

  emit('select', item);
}
</script>

<i18n lang="json">
{
  "en": {
    "search_placeholder": "Search",
    "empty_title": "No results found",
    "search_empty_description": "Try searching a different term"
  }
}
</i18n>
